import { useState } from 'react';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import type { RegisterBridgeTenantInput } from '../../../infrastructure/BridgeTenantClient';
import { BridgeTenantOrigins } from './BridgeTenantOrigins';

interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (input: RegisterBridgeTenantInput) => Promise<void>;
}

/**
 * Registers one brand. Origins are validated here rather than left to the server, because a bare
 * 400 mid-onboarding does not tell the operator which of four fields was wrong.
 */
export default function RegisterBrandDialog({ show, onClose, onSubmit }: Props) {
  const [brandName, setBrandName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [shopDomain, setShopDomain] = useState('');
  const [originDraft, setOriginDraft] = useState('');
  const [origins, setOrigins] = useState<string[]>([]);
  const [originError, setOriginError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setBrandName('');
    setTenantId('');
    setShopDomain('');
    setOriginDraft('');
    setOrigins([]);
    setOriginError(null);
    setSubmitError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const addOrigin = () => {
    const result = BridgeTenantOrigins.add(origins, originDraft);
    setOrigins(result.origins);
    setOriginError(result.error);
    if (!result.error) {
      setOriginDraft('');
    }
  };

  const removeOrigin = (origin: string) => setOrigins(origins.filter((value) => value !== origin));

  const canSubmit =
    brandName.trim().length > 0 &&
    tenantId.trim().length > 0 &&
    BridgeTenantOrigins.isSubmittable(origins) &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit({
        tenantId: tenantId.trim(),
        brandName: brandName.trim(),
        shopDomain: shopDomain.trim(),
        allowedOrigins: origins,
      });
      reset();
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : '등록에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">브랜드 등록</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          등록하면 이 브랜드의 이벤트만 저장된다. 등록 전에 도착한 이벤트는 복구되지 않는다.
        </p>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-medium">
            브랜드명 <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            value={brandName}
            onChange={(event) => setBrandName(event.target.value)}
            placeholder="Eqqual Berry Global"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-medium">
            Tenant ID <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            placeholder="eqqual-berry"
          />
          <Form.Text className="text-muted">브랜드 페이지 소스에 그대로 노출된다. 비밀이 아니라 식별자다.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="small fw-medium">Shop 도메인</Form.Label>
          <Form.Control
            value={shopDomain}
            onChange={(event) => setShopDomain(event.target.value)}
            placeholder="eqqual.myshopify.com"
          />
          <Form.Text className="text-muted">앱스토어 설치 시 자동으로 채워진다. 파일럿은 비워도 된다.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label className="small fw-medium">
            허용 Origin <span className="text-danger">*</span>
          </Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              value={originDraft}
              isInvalid={Boolean(originError)}
              onChange={(event) => setOriginDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addOrigin();
                }
              }}
              placeholder="https://eqqual.myshopify.com"
            />
            <Button variant="outline-secondary" onClick={addOrigin}>
              추가
            </Button>
          </div>
          {originError && <div className="text-danger small mt-1">{originError}</div>}
          {origins.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {origins.map((origin) => (
                <Badge
                  key={origin}
                  bg="light"
                  text="dark"
                  className="border d-inline-flex align-items-center gap-2 fw-normal"
                >
                  <span className="font-monospace">{origin}</span>
                  <button
                    type="button"
                    className="btn-close btn-close-sm p-0 border-0 bg-transparent lh-1"
                    aria-label={`${origin} 제거`}
                    onClick={() => removeOrigin(origin)}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <Form.Text className="text-muted d-block mt-2">
            브라우저는 이 헤더를 위조할 수 없다. 위조를 거르는 유일한 수단이다. <strong>비우면 전부 거부</strong>된다.
          </Form.Text>
        </Form.Group>

        {submitError && <div className="alert alert-danger py-2 px-3 small mb-0 mt-3">{submitError}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={close} disabled={submitting}>
          취소
        </Button>
        <Button variant="dark" onClick={submit} disabled={!canSubmit}>
          {submitting ? '등록 중…' : '등록'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
